import * as React from 'react';

import Button from '../../../../shared/components/Button';
import DateRange from '../../../../shared/components/DateRange';
import QuantityControl from '../../../../shared/components/QuantityControl';
import { usePlatformData } from '../../../home/hooks/usePlatformData';
import PassConfigurationModal from '../../../product/components/PassConfigurationModal';

import RemovePassModal from './removePassModal';

export interface BasketItemProps {
  title: string;
  children?: React.ReactNode;
  addOns?: Array<{ id?: string; name: string; price?: number; totalPrice?: number }>;
  price: number;
  originalPrice?: number;
  quantity?: number;
  onQuantityChange?: (qty: number) => void;
  onRemove?: () => void;
  promoLabel?: string;
  promoValue?: string;
  currency?: string;
  locale?: string;
  startDate?: string;
  endDate?: string;
  venue?: string;
  location?: string;
  rawOptions?: unknown[];
  rawAddOns?: unknown[];
  selectedOption?: unknown;
  selectedOptions?: string[];
  isMultiOption?: boolean;
  onConfigureAddOns?: (payload: {
    lineItemId?: string;
    productId?: string;
    selectedAddOnIds: string[];
    quantity: number;
    selectedOption: string[];
  }) => void;
  lineItemId?: string;
  productId?: string;
  attachedAddOnIds?: string[];
  showAddOns?: boolean;
  showOptions?: boolean;
}

const BasketItem = ({
  title,
  children,
  addOns = [],
  price,
  originalPrice,
  quantity = 1,
  onQuantityChange,
  onRemove,
  promoLabel,
  promoValue,
  currency = 'USD',
  locale = 'en',
  startDate,
  endDate,
  venue,
  location,
  rawOptions = [],
  rawAddOns = [],
  selectedOption = null,
  selectedOptions = [],
  isMultiOption = false, // New prop to control selection mode
  onConfigureAddOns,
  lineItemId,
  productId,
  attachedAddOnIds = [], // Add this prop - actual add-on IDs from cart
  showAddOns = true, // Toggle visibility of add-ons
  showOptions = true, // Toggle visibility of options
}: BasketItemProps) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [showRemovePassModal, setShowRemovePassModal] = React.useState(false);
  const [modalSelectedAddOns, setModalSelectedAddOns] = React.useState<Record<string, boolean>>({});
  const [modalQuantity, setModalQuantity] = React.useState(quantity ?? 1);
  // Initialize with array if present, else fallback to wrapping legacy option
  const [modalSelectedOptions, setModalSelectedOptions] = React.useState<string[]>(
    Array.isArray(selectedOptions) && selectedOptions.length > 0
      ? selectedOptions
      : (typeof selectedOption === 'string' ? [selectedOption] : [])
  );
  const { eventData: _eventData } = usePlatformData();
  const [initialModalState, setInitialModalState] = React.useState<{
    selectedAddOns: Record<string, boolean>;
    selectedOptions: string[];
    quantity: number;
  } | null>(null);

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDateRange = (start: string | null | undefined, end: string | null | undefined) => {
    if (!start && !end) return '';

    const parseDate = (dateStr: string | null | undefined) => {
      if (!dateStr) return null;
      return new Date(dateStr);
    };

    const start_date = parseDate(start);
    const end_date = parseDate(end);

    if (!start_date && !end_date) return '';
    if (!end_date) return start_date!.toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' });
    if (!start_date) return end_date.toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' });

    const options_short: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    const options_full: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };

    return `${start_date.toLocaleDateString('en', options_short)} - ${end_date.toLocaleDateString('en', options_full)}`;
  };

  const hasAddOns = addOns && addOns.length > 0;
  const hasAvailableAddOns = rawAddOns && rawAddOns.length > 0;

  const handleDecrease = () => {
    if (quantity === 1) {
      setShowRemovePassModal(true);
    } else if (onQuantityChange) {
      onQuantityChange(quantity - 1);
    }
  };


  const handleKeepPass = () => {
    setShowRemovePassModal(false);
  };

  const handleRemovePass = () => {
    setShowRemovePassModal(false);
    if (quantity > 1 && onQuantityChange) {
      onQuantityChange(quantity - 1);
    } else if (onRemove) {
      onRemove();
    }
  };

  const handleIncrease = () => {
    if (onQuantityChange) {
      onQuantityChange(quantity + 1);
    }
  };

  const handleOpenConfigureModal = () => {
    // Initialize modal selections with currently attached add-on IDs
    const initialSelections: Record<string, boolean> = {};
    (rawAddOns as Array<{ id: string }>)?.forEach(addon => {
      initialSelections[addon.id] = attachedAddOnIds.includes(addon.id);
    });
    setModalSelectedAddOns(initialSelections);
    setModalQuantity(quantity);

    const initialOptions = Array.isArray(selectedOptions) && selectedOptions.length > 0
      ? selectedOptions
      : (typeof selectedOption === 'string' ? [selectedOption] : []);

    setModalSelectedOptions(initialOptions);

    // Save initial state to track changes
    setInitialModalState({
      selectedAddOns: initialSelections,
      selectedOptions: initialOptions,
      quantity: quantity
    });
    setIsModalOpen(true);
  };

  const handleCloseConfigureModal = () => {
    setIsModalOpen(false);
    setInitialModalState(null);
  };

  // Check if there are any changes from initial state
  const hasChanges = () => {
    if (!initialModalState) return false;

    // Check if selected add-ons changed
    const addOnsChanged = JSON.stringify(modalSelectedAddOns) !== JSON.stringify(initialModalState.selectedAddOns);

    // Check if selected option changed - compare sorted arrays
    const optsChanged = JSON.stringify([...modalSelectedOptions].sort()) !==
      JSON.stringify([...(initialModalState.selectedOptions || [])].sort());

    // Check if quantity changed
    const quantityChanged = modalQuantity !== initialModalState.quantity;

    return addOnsChanged || optsChanged || quantityChanged;
  };

  const handleAddOnSelectionChange = (addOnId: string, isSelected: boolean) => {
    setModalSelectedAddOns(prev => ({
      ...prev,
      [addOnId]: isSelected
    }));
  };

  const handleOptionChange = (options: string[]) => {
    // options is now an array from PassTypes
    setModalSelectedOptions(options);
  };

  const handleConfigureAddOns = (_selection: { addOns: unknown[]; options: unknown[] }) => {
    // Selection from PassConfigurationModal can be used, but local state is already synced
    const selectedAddOnIds = Object.keys(modalSelectedAddOns).filter(id => modalSelectedAddOns[id]);

    // Final option is the array of selected options
    const finalOption = modalSelectedOptions;

    if (onConfigureAddOns) {
      onConfigureAddOns({
        lineItemId,
        productId,
        selectedAddOnIds,
        quantity: modalQuantity,
        selectedOption: finalOption
      });
    }
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="bg-white border border-[#D0D5DD] rounded-lg p-6 shadow-sm w-full md:max-w-[810px]">      {/* Date/Location Row */}
        <div className="flex justify-between items-start mb-2">
          <div>
            {startDate || endDate || venue || location ? (
              <div className="text-xs text-gray-500">
                <span className="block sm:inline">
                  {/* Mobile View: Stacked Dates if Multi-day */}
                  <span className="block sm:hidden">
                    {(() => {
                      const start = startDate ? new Date(startDate) : null;
                      const end = endDate ? new Date(endDate) : null;
                      const isMultiDay = start && end && start.toDateString() !== end.toDateString();

                      if (isMultiDay) {
                        return (
                          <span className="flex flex-col">
                            <DateRange
                              startDate={startDate}
                              endDate={null}
                              locale={locale}
                              format="short"
                              className="text-xs text-gray-500 leading-tight"
                            />
                            <DateRange
                              startDate={endDate}
                              endDate={null}
                              locale={locale}
                              format="short"
                              className="text-xs text-gray-500 leading-tight"
                            />
                          </span>
                        );
                      }
                      // Single day or invalid: fallthrough to standard
                      return (
                        <DateRange
                          startDate={startDate}
                          endDate={endDate}
                          locale={locale}
                          format="short"
                          className="text-xs text-gray-500"
                        />
                      );
                    })()}
                  </span>

                  {/* Desktop View: Always standard */}
                  <span className="hidden sm:inline">
                    <DateRange
                      startDate={startDate}
                      endDate={endDate}
                      locale={locale}
                      format="short"
                      className="text-xs text-gray-500"
                    />
                  </span>
                </span>
                {(venue || location) && (
                  <>
                    <span className="hidden sm:inline"> • </span>
                    <span className="block sm:inline mt-1 sm:mt-0">
                      {[venue, location].filter(Boolean).join(', ')}
                    </span>
                  </>
                )}
              </div>
            ) : null}
            {children}
          </div>

          {promoLabel && promoValue && (
            <div className="flex items-center justify-center w-[110px] h-[26px] gap-[4px] ml-4 bg-[#F9FAFB] rounded-[8px] pt-1 pr-2 pb-1 pl-2">
              <span className="text-[#667085] text-xs font-normal leading-none text-center">
                {promoLabel}:
              </span>
              <span className="text-[#030407] text-xs font-normal leading-none text-center">
                {promoValue}
              </span>
            </div>
          )}
        </div>

        {/* Title Row */}
        <div className="mb-2">
          <h3 className="text-2xl font-bold text-gray-900 m-0 leading-tight">{title}</h3>
        </div>

        {/* Add-ons Section */}
        {hasAddOns && (
          <div className="mb-3 flex flex-col gap-1">
            {addOns.map((addOn, index) => {
              const addonPrice = typeof addOn.price === 'number'
                ? addOn.price
                : ((addOn.totalPrice || 0) / 100); // Total price from GroupedMainItem is centAmount
              return (
                <div key={index} className="flex justify-between items-center text-xs font-normal leading-[1.5] tracking-[0px]">
                  <span className="text-[#030407]">+ {addOn.name}</span>
                  <span className="text-xs font-normal text-gray-500 leading-[1.5] tracking-[0px] text-right">
                    {formatPrice(addonPrice)}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Configure Button - Show if available add-ons exist AND at least one toggle is enabled */}
        {hasAvailableAddOns && (showAddOns || showOptions) && (
          <div className="mb-3">
            <Button
              variant="secondary"
              className="inline-flex items-center gap-2 py-2 px-3 bg-white border border-gray-200 rounded text-gray-700 text-sm font-medium hover:bg-gray-50 focus:ring-2 focus:ring-offset-1 focus:ring-gray-200 shadow-sm"
              type="button"
              onClick={handleOpenConfigureModal}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.5 11.5H3.16667L9.91667 4.75L9.25 4.08333L2.5 10.8333V11.5ZM1.33333 12.6667V10.3333L10.0833 1.58333C10.2917 1.375 10.5347 1.27083 10.8125 1.27083C11.0903 1.27083 11.3333 1.375 11.5417 1.58333L12.4167 2.45833C12.625 2.66667 12.7292 2.90972 12.7292 3.1875C12.7292 3.46528 12.625 3.70833 12.4167 3.91667L3.66667 12.6667H1.33333ZM11.7292 3.22917L10.7708 2.27083L11.7292 3.22917ZM8.83333 5.16667L9.25 4.08333L9.91667 4.75L8.83333 5.16667Z" fill="#374151" />
              </svg>
              Configure add ons
            </Button>
          </div>
        )}

        <div className="flex justify-between items-center pt-2">
          <div className="flex items-center gap-1.5">
            <QuantityControl
              quantity={quantity}
              onDecrease={handleDecrease}
              onIncrease={handleIncrease}
              size="lg"
            />

          </div>
          <div className="flex flex-col items-end">
            {/* Show original price if there's a discount */}
            {originalPrice && originalPrice !== price && (
              <div className="text-xs font-normal text-[#667085] line-through leading-[1.5] tracking-[0px] text-right">
                {formatPrice(originalPrice)}
              </div>
            )}
            <div className="text-lg font-semibold text-[#030407] leading-tight tracking-[0px] text-right">{formatPrice(price)}</div>
          </div>
        </div>


      </div>

      {/* Pass Configuration Modal */}
      <PassConfigurationModal
        isOpen={isModalOpen}
        onClose={handleCloseConfigureModal}
        title={title}
        description={formatDateRange(startDate, endDate)}
        price={Math.round(price * 100)}
        originalPrice={originalPrice != null ? Math.round(originalPrice * 100) : undefined}
        currency={currency}
        rawOptions={rawOptions}
        rawAddOns={rawAddOns}
        activeCurrency={currency}
        isMultiOption={isMultiOption}
        selectedOptions={modalSelectedOptions}
        onOptionChange={handleOptionChange}
        selectedAddOns={modalSelectedAddOns}
        onSelectionChange={handleAddOnSelectionChange}
        onAddToBasket={handleConfigureAddOns}
        buttonText="Update pass"
        quantity={modalQuantity}
        onQuantityChange={setModalQuantity}
        disabled={false}
        buttonDisabled={!hasChanges()}
        hidePassDetails={true}
        showAddOns={showAddOns}
        showOptions={showOptions}
      />
      {/* Remove Pass Modal */}
      <RemovePassModal
        open={showRemovePassModal}
        onKeep={handleKeepPass}
        onRemove={handleRemovePass}
      />
    </>
  );
};

export default BasketItem;
